<script setup lang="ts">
import { computed } from 'vue'
import { Account } from '../../firebase/services/accounts.service'
import { useBankStore } from '../../stores/bank'

const props = defineProps<{
  accounts: Account[]
  loading?: boolean
}>()

const bankStore = useBankStore()
bankStore.fetchBanks()

// Saldos iniciais positivos (créditos)
const initialCredits = computed(() => {
  return bankStore.banks.reduce((acc, bank) => {
    const balance = bank.initialBalance || 0
    return acc + (balance > 0 ? balance : 0)
  }, 0)
})

// Saldos iniciais negativos (débitos)
const initialDebits = computed(() => {
  return bankStore.banks.reduce((acc, bank) => {
    const balance = bank.initialBalance || 0
    return acc + (balance < 0 ? Math.abs(balance) : 0)
  }, 0)
})

// Função para determinar o status real da conta
function determineAccountStatus(account) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const dueDate = new Date(account.dueDate)
  dueDate.setHours(0, 0, 0, 0)
  
  const scheduledDate = account.scheduledDate ? new Date(account.scheduledDate) : null
  if (scheduledDate) scheduledDate.setHours(0, 0, 0, 0)
  
  // Se a conta está cancelada
  if (account.status === 'CANCELADO') return 'CANCELADO'
  
  // Se tem data agendada
  if (scheduledDate) {
    // Se a data agendada já passou, conta está paga
    if (scheduledDate < today) {
      return 'PAGO'
    }
    // Se a data agendada é hoje ou futura, conta está agendada
    return 'AGENDADO'
  }
  
  // Se não tem data agendada, verifica o vencimento
  if (dueDate < today) {
    return 'ATRASADO'
  }
  
  // Se a data de vencimento é hoje ou futura, está pendente
  return 'PENDENTE'
}

// Contas a receber vencidas ou que vencem hoje
const currentReceivables = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const receivables = props.accounts
    .filter(account => {
      // Considerar apenas contas PAGAS
      if (determineAccountStatus(account) !== 'PAGO') {
        return false
      }
      return account.type === 'receivable'
    })
    .reduce((acc, account) => acc + account.value, 0)

  return receivables + initialCredits.value
})

// Contas a pagar vencidas ou que vencem hoje
const currentPayables = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const payables = props.accounts
    .filter(account => {
      // Considerar apenas contas PAGAS
      if (determineAccountStatus(account) !== 'PAGO') {
        return false
      }
      return account.type === 'payable'
    })
    .reduce((acc, account) => acc + account.value, 0)

  return payables + initialDebits.value
})

// Saldo atual (créditos - débitos)
const currentBalance = computed(() => {
  return currentReceivables.value - currentPayables.value
})

// Contas a receber futuras (não pagas)
const futureReceivables = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return props.accounts
    .filter(account => {
      const status = determineAccountStatus(account)
      return status !== 'PAGO' && status !== 'CANCELADO' && account.type === 'receivable'
    })
    .reduce((acc, account) => acc + account.value, 0)
})

// Contas a pagar futuras (não pagas)
const futurePayables = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return props.accounts
    .filter(account => {
      const status = determineAccountStatus(account)
      return status !== 'PAGO' && status !== 'CANCELADO' && account.type === 'payable'
    })
    .reduce((acc, account) => acc + account.value, 0)
})

// Saldo projetado (saldo atual + recebimentos futuros - pagamentos futuros)
const projectedBalance = computed(() => {
  return currentBalance.value + futureReceivables.value - futurePayables.value
})

// Percentuais para as barras de progresso
const currentPercentages = computed(() => {
  const total = currentReceivables.value + currentPayables.value
  return {
    credits: total > 0 ? (currentReceivables.value / total) * 100 : 0,
    debits: total > 0 ? (currentPayables.value / total) * 100 : 0
  }
})

const projectedPercentages = computed(() => {
  const total = futureReceivables.value + futurePayables.value
  return {
    credits: total > 0 ? (futureReceivables.value / total) * 100 : 0,
    debits: total > 0 ? (futurePayables.value / total) * 100 : 0
  }
})

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}
</script>

<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
    <!-- Saldo Atual -->
    <div class="bg-white rounded-lg shadow p-4">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-gray-900">Saldo Atual</h2>
        <span :class="[
          'text-2xl sm:text-3xl font-bold',
          currentBalance >= 0 ? 'text-green-600' : 'text-red-600'
        ]">
          {{ formatCurrency(currentBalance) }}
        </span>
      </div>
      
      <div class="grid grid-cols-2 gap-4 mb-4">
        <!-- Mini-card Créditos -->
        <div class="bg-gray-50 rounded-lg p-3">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm text-gray-500">Créditos</span>
            <i class="pi pi-arrow-down text-green-500 text-sm"></i>
          </div>
          <span class="text-base font-medium text-green-600">
            {{ formatCurrency(currentReceivables) }}
          </span>
        </div>

        <!-- Mini-card Débitos -->
        <div class="bg-gray-50 rounded-lg p-3">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm text-gray-500">Débitos</span>
            <i class="pi pi-arrow-up text-red-500 text-sm"></i>
          </div>
          <span class="text-base font-medium text-red-600">
            {{ formatCurrency(currentPayables) }}
          </span>
        </div>
      </div>

      <!-- Barras de Progresso -->
      <div class="space-y-3">
        <!-- Barra de Créditos -->
        <div>
          <div class="flex justify-between text-xs text-gray-500 mb-1">
            <span>Créditos</span>
            <span>{{ currentPercentages.credits.toFixed(1) }}%</span>
          </div>
          <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              class="h-full bg-green-500 transition-all duration-300"
              :style="{ width: `${currentPercentages.credits}%` }"
            ></div>
          </div>
        </div>

        <!-- Barra de Débitos -->
        <div>
          <div class="flex justify-between text-xs text-gray-500 mb-1">
            <span>Débitos</span>
            <span>{{ currentPercentages.debits.toFixed(1) }}%</span>
          </div>
          <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              class="h-full bg-red-500 transition-all duration-300"
              :style="{ width: `${currentPercentages.debits}%` }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Saldo Projetado -->
    <div class="bg-white rounded-lg shadow p-4">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-gray-900">Saldo Projetado</h2>
        <span :class="[
          'text-2xl sm:text-3xl font-bold',
          projectedBalance >= 0 ? 'text-green-600' : 'text-red-600'
        ]">
          {{ formatCurrency(projectedBalance) }}
        </span>
      </div>

      <div class="grid grid-cols-2 gap-4 mb-4">
        <!-- Projeção de Recebimentos -->
        <div class="bg-gray-50 rounded-lg p-3">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm text-gray-500">A Receber</span>
            <i class="pi pi-arrow-down text-green-500 text-sm"></i>
          </div>
          <span class="text-base font-medium text-green-600">
            {{ formatCurrency(futureReceivables) }}
          </span>
        </div>

        <!-- Projeção de Pagamentos -->
        <div class="bg-gray-50 rounded-lg p-3">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm text-gray-500">A Pagar</span>
            <i class="pi pi-arrow-up text-red-500 text-sm"></i>
          </div>
          <span class="text-base font-medium text-red-600">
            {{ formatCurrency(futurePayables) }}
          </span>
        </div>
      </div>

      <!-- Barras de Progresso -->
      <div class="space-y-3">
        <!-- Barra de Créditos -->
        <div>
          <div class="flex justify-between text-xs text-gray-500 mb-1">
            <span>Créditos</span>
            <span>{{ projectedPercentages.credits.toFixed(1) }}%</span>
          </div>
          <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              class="h-full bg-green-500 transition-all duration-300"
              :style="{ width: `${projectedPercentages.credits}%` }"
            ></div>
          </div>
        </div>

        <!-- Barra de Débitos -->
        <div>
          <div class="flex justify-between text-xs text-gray-500 mb-1">
            <span>Débitos</span>
            <span>{{ projectedPercentages.debits.toFixed(1) }}%</span>
          </div>
          <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              class="h-full bg-red-500 transition-all duration-300"
              :style="{ width: `${projectedPercentages.debits}%` }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>